import { Server } from './server';
import { Service, ServiceResponse } from './service';

const BLOGS_CACHE_EXPIRATION = 60 * 60 * 24;
const BLOGS_METHOD = 'blogs/v3';
const PUBLIC_METHOD = 'public/v3';

export class BlogsService extends Service {
  constructor() {
    super();
  }

  public async init(): Promise<ServiceResponse> {
    await Server.database.purge('BLOGS#', BLOGS_CACHE_EXPIRATION);
    await Server.database.purge('BLOG#', BLOGS_CACHE_EXPIRATION);
    await Server.database.purge('BLOGCOMMENTS#', BLOGS_CACHE_EXPIRATION);
    return {success: true};
  }

  // cache
  public async setBlogsCache(category:string, blogs:any[], update:boolean = false) {
    await Server.database.putItem(`BLOGS#${category}`, blogs, update);
  }

  public async getBlogsCache(category:string) {
    return await Server.database.getItem(`BLOGS#${category}`, BLOGS_CACHE_EXPIRATION);
  }

  public async clearBlogsCache() {
    await Server.database.purge('BLOGS#');
  }

  public async setBlogInCache(blog:any) {
    await Server.database.putItem(`BLOG#${blog.id}`, blog);
  }

  public async getBlogFromCache(id:string) {
    return await Server.database.getItem(`BLOG#${id}`, BLOGS_CACHE_EXPIRATION);
  }

  public async clearBlogFromCache(id:string) {
    await Server.database.removeItem(`BLOG#${id}`);
  }

  public async getBlogFromCacheBySlug(slug:string) {
    let blogs = await Server.database.query(`BLOG#`, BLOGS_CACHE_EXPIRATION);
    let blog = blogs.find(b => b.slug == slug);
    return blog;
  }

  public async setBlogCommentsInCache(id:string, sort:boolean, cache:any) {
    await Server.database.putItem(`BLOGCOMMENTS#${id}#${sort}`, cache);
  }

  public async getBlogCommentsFromCache(id:string, sort:boolean) {
    return await Server.database.getItem(`BLOGCOMMENTS#${id}#${sort}`, BLOGS_CACHE_EXPIRATION);
  }

  public async clearBlogCommentsCache(id:string) {
    await Server.database.purge('BLOGCOMMENTS#');
  }

  // helpers
  protected async sendBlogsCommand(command:string, params:any, data?:string) {
    let response = await this.sendCommand(BLOGS_METHOD, command, params);
    if(response.status != 200)
      return {success: false, message: response.body.message};
    let ret:any = {success: true};
    if(data)
      ret[data] = response.body[data];
    return ret;
  }

  // blogs
  public async createBlog(params:{slug: string, title: string, content: string, tags?:string, image?: string}) {
    return await this.sendBlogsCommand('create-blog', params, 'id');
  }

  public async editBlog(params:{id: string, slug?: string, title?: string, content?: string, tags?:string, image?: string}) {
    return await this.sendBlogsCommand('edit-blog', params);
  }

  public async archiveBlog(id:string) {
    return await this.sendBlogsCommand('archive-blog', {id});
  }

  public async restoreBlog(id:string) {
    return await this.sendBlogsCommand('restore-blog', {id});
  }

  public async publishBlog(id:string, notify:boolean=false) {
    return await this.sendBlogsCommand('publish-blog', {id, notify});
  }

  public async unpublishBlog(id:string) {
    return await this.sendBlogsCommand('unpublish-blog', {id});
  }

  public async deleteBlog(id:string) {
    return await this.sendBlogsCommand('delete-blog', {id});
  }

  public async getBlog(params:{id?:string, slug?:string}) {
    let method = Server.isLoggedIn() ? BLOGS_METHOD : PUBLIC_METHOD;

    let response = await this.sendCommand(method, 'get-blog', params);

    if(response.status != 200)
      return {success: false, message: response.body.message};

    return {success: true, blog: response.body.blog};
  }

  public async getBlogComments(params:{id?:string, sort?:boolean, more?:any, after?:any}) {
    let method = Server.isLoggedIn() ? BLOGS_METHOD : PUBLIC_METHOD;

    let response = await this.sendCommand(method, 'get-blog-comments', params);

    if(response.status != 200)
      return {success: false, message: response.body.message};

    return {success: true, comments: response.body.comments, more: response.body.more};
  }

  public async getBlogs(params:{author?:string, filter?:string, state?:string, before?:any, after?:any} = {}) {
    let method = Server.isLoggedIn() ? BLOGS_METHOD : PUBLIC_METHOD;

    let response = await this.sendCommand(method, 'get-blogs', params);

    if(response.status != 200)
      return {success: false, message: response.body.message};

    return {success: true, blogs: response.body.blogs};
  }

  // comments
  public async createComment(params:{id:string, content: string}) {
    return await this.sendBlogsCommand('create-comment', params, 'date');
  }

  public async editComment(params:{id:string, date:string, content:string, user?:string}) {
    return await this.sendBlogsCommand('edit-comment', params);
  }

  public async deleteComment(params:{id:string, date:string, user?:string}) {
    return await this.sendBlogsCommand('delete-comment', params);
  }

  
}

